<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { listRecords } from '@/lib/records.js'
import { rememberLabel } from '@/lib/offline.js'
import { ENTITIES } from '@/lib/entities.js'
import { useSession } from '@/stores/session.js'
import { useDebounce } from '@/composables/useDebounce.js'
import { useUi } from '@/stores/ui.js'
import { entityLabel } from '@/lib/labels.js'
import { prefillFromText } from '@/lib/quickCreate.js'
import { useFloating } from '@/composables/useFloating.js'

/**
 * Pick one record of an entity by typing fragments of its name (D-11). Online it asks Core,
 * offline the replica. v-model is the uuid; `reference` is the {uuid,label} shown until the
 * person picks something else.
 *
 * A record that is not there yet is created from here (Dynamics' "+ New"): a panel opens over
 * everything with what was typed as its name and `createDefaults` (what the surrounding form
 * knows), and once saved the new record is put into this field. Off in filters.
 *
 * The results float over the whole page by the field (useFloating): no dialog or scrolling area
 * cuts them off, as wide as the field.
 */
const props = defineProps({
  entity: { type: String, required: true },
  modelValue: { type: String, default: null },
  reference: { type: Object, default: null },
  placeholder: { type: String, default: '' },
  disabled: Boolean,
  filter: { type: Object, default: () => ({}) },
  invalid: Boolean,
  id: { type: String, default: '' },
  allowCreate: { type: Boolean, default: true },
  createDefaults: { type: Object, default: () => ({}) },
  // What the new record is created for, shown on the panel ("Nowa szansa").
  createFrom: { type: String, default: '' },
  // Records to offer first, under a heading: { filter, title } — the contacts of the account the
  // form names. The rest follow under "Others"; a suggested one is picked with `_suggested`.
  suggest: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'select'])

const session = useSession()
const ui = useUi()
const query = ref('')
const { open, anchor: root, floating, style, show, close, place } = useFloating()
const width = ref(0)
const listStyle = computed(() => ({ ...style.value, width: `${width.value}px` }))
const loading = ref(false)
const results = ref([])
const active = ref(0)
const chosen = ref(props.reference)

watch(() => props.reference, (v) => {
  if (v) chosen.value = v
})
watch(() => props.modelValue, (v) => {
  if (!v) chosen.value = null
})

const label = computed(() => (props.modelValue ? chosen.value?.label || '…' : ''))

const suggestedCount = ref(0)
// What the results shown were found for: till they are for what is typed, it is still searching.
const found = ref(null)
const searching = computed(() => loading.value || found.value !== query.value)
const search = useDebounce(async () => {
  const asked = query.value
  loading.value = true
  try {
    const q = asked || undefined
    const [first, all] = await Promise.all([
      props.suggest ? listRecords(props.entity, { filter: { ...props.filter, ...props.suggest.filter, q }, page: { size: 8 } }, { me: session.me }).catch(() => ({ data: [] })) : { data: [] },
      listRecords(props.entity, { filter: { ...props.filter, q }, page: { size: 8 } }, { me: session.me }),
    ])
    if (asked !== query.value) return
    found.value = asked
    const mine = first.data.map((r) => ({ ...r, _suggested: true }))
    const seen = new Set(mine.map((r) => r.uuid))
    suggestedCount.value = mine.length
    results.value = [...mine, ...all.data.filter((r) => !seen.has(r.uuid))]
    active.value = 0
    // Taller or shorter now: placed again by the field.
    if (open.value) nextTick(place)
  } catch {
    results.value = []
    suggestedCount.value = 0
    found.value = asked
  } finally {
    loading.value = false
  }
}, 220)

const canCreate = computed(() => props.allowCreate && !props.disabled && session.has(`${props.entity}:create`))
// The "create" row is the last option: arrows reach it, Enter on it creates.
const createIndex = computed(() => (canCreate.value ? results.value.length : -1))

function create() {
  const typed = query.value
  close()
  ui.openCreate({
    entity: props.entity,
    // What was typed wins over what the form suggests.
    defaults: { ...props.createDefaults, ...prefillFromText(props.entity, typed) },
    from: props.createFrom,
    onSaved: (record) => pick(record),
  })
}

function start() {
  if (props.disabled) return
  if (!open.value) {
    width.value = root.value?.offsetWidth || 0
    show()
  }
  search()
}
function pick(record) {
  const ref_ = { uuid: record.uuid, label: record.label, number: record.number, entity: props.entity }
  chosen.value = ref_
  rememberLabel(ref_)
  emit('update:modelValue', record.uuid)
  emit('select', record)
  close()
  query.value = ''
}
function clear() {
  chosen.value = null
  emit('update:modelValue', null)
  emit('select', null)
}
function onKey(e) {
  const last = canCreate.value ? results.value.length : results.value.length - 1
  if (e.key === 'ArrowDown') { active.value = Math.min(active.value + 1, last); e.preventDefault() }
  else if (e.key === 'ArrowUp') { active.value = Math.max(active.value - 1, 0); e.preventDefault() }
  else if (e.key === 'Enter' && active.value === createIndex.value) { create(); e.preventDefault() }
  else if (e.key === 'Enter' && results.value[active.value]) { pick(results.value[active.value]); e.preventDefault() }
  else if (e.key === 'Escape') close()
}
</script>

<template>
  <div ref="root" class="relative">
    <div v-if="modelValue && !open" :class="['input flex items-center gap-2', invalid ? 'invalid' : '', disabled ? 'opacity-70' : '']">
      <i :class="['fa-solid', ENTITIES[entity]?.icon, 'text-xs', ENTITIES[entity]?.color]" aria-hidden="true"></i>
      <RecordLink :entity="entity" :uuid="modelValue" class="link min-w-0 flex-1 truncate">{{ label }}</RecordLink>
      <button v-if="!disabled" type="button" class="text-faint hover:text-[var(--text)]" :aria-label="$t('common.edit')" @click="start"><i class="fa-solid fa-pen text-xs" aria-hidden="true"></i></button>
      <button v-if="!disabled" type="button" class="text-faint hover:text-red-600" :aria-label="$t('common.clear')" @click="clear"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>
    <div v-else class="relative">
      <input
        :id="id || undefined"
        v-model="query"
        type="search"
        autocomplete="off"
        :class="['input pr-8', invalid ? 'invalid' : '']"
        :placeholder="placeholder || $t('common.searchPlaceholder')"
        :disabled="disabled"
        role="combobox"
        :aria-expanded="open"
        @focus="start"
        @input="start"
        @keydown="onKey"
      />
      <i :class="['fa-solid absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-faint', loading ? 'fa-circle-notch fa-spin' : 'fa-magnifying-glass']" aria-hidden="true"></i>
    </div>
    <!-- The results scroll; "+ New" stays pinned under them, always in sight (as in Dynamics). -->
    <Teleport to="body">
    <div v-if="open" ref="floating" class="surface fixed flex max-h-80 flex-col overflow-hidden rounded-xl border border-default shadow-xl" :style="listStyle" role="listbox">
      <div class="min-h-0 flex-1 overflow-y-auto py-1">
        <p v-if="searching && results.length === 0" class="px-3 py-2 text-sm text-faint" data-testid="picker-searching"><i class="fa-solid fa-circle-notch fa-spin mr-1.5 text-xs" aria-hidden="true"></i>{{ $t('common.searching') }}</p>
        <p v-else-if="results.length === 0" class="px-3 py-2 text-sm text-faint">{{ $t('common.nothingFound') }}</p>
        <template v-for="(r, i) in results" :key="r.uuid">
        <p v-if="suggest && i === 0 && suggestedCount" class="px-3 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wide text-faint" data-testid="ref-suggested">{{ suggest.title }}</p>
        <p v-if="suggest && suggestedCount && i === suggestedCount" class="border-t border-default px-3 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wide text-faint">{{ $t('common.others') }}</p>
        <button
          type="button"
          role="option"
          :aria-selected="i === active"
          :class="['flex w-full items-center gap-2 px-3 py-2 text-left text-sm', i === active ? 'bg-brand-50 dark:bg-brand-900/40' : 'hover:bg-[var(--surface-3)]']"
          @mousedown.prevent="pick(r)"
        >
          <span class="min-w-0 flex-1 truncate">{{ r.label }}</span>
          <span v-if="suggest && !r._suggested && r.account?.label" class="max-w-[40%] truncate text-xs text-faint">{{ r.account.label }}</span>
          <span class="text-xs text-faint">{{ r.number }}</span>
        </button>
        </template>
      </div>
      <button
        v-if="canCreate"
        type="button"
        role="option"
        :aria-selected="active === createIndex"
        :data-testid="`ref-create-${entity}`"
        :class="['flex w-full shrink-0 items-center gap-2 border-t border-default px-3 py-2 text-left text-sm font-medium text-brand-700 dark:text-brand-300', active === createIndex ? 'bg-brand-50 dark:bg-brand-900/40' : 'hover:bg-[var(--surface-3)]']"
        @mousedown.prevent="create"
      >
        <i class="fa-solid fa-plus fa-fw" aria-hidden="true"></i>
        <span class="min-w-0 flex-1 truncate">{{ query.trim() ? $t('create.newNamed', { entity: entityLabel(entity, 'new'), name: query.trim() }) : entityLabel(entity, 'new') }}</span>
      </button>
    </div>
    </Teleport>
  </div>
</template>
